import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Autocomplete,
    TextField,
    ToggleButtonGroup,
    ToggleButton,
    Grid,
    Typography,
    Divider,
    IconButton,
    Box,
    Tooltip,
} from '@mui/material';
import {
    DragIndicator,
    Add,
    Close,
    ArrowUpward,
    ArrowDownward,
} from '@mui/icons-material';
import {
    DndContext,
    closestCenter,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    SortableContext,
    useSortable,
    verticalListSortingStrategy,
    arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';


export const EMPLOYEE_FILTER_EMPTY = {
    employeeCode: '',
    employeeName: '',
    employeeTypes: [],
    genders: [],
    workingModes: [],
    ranks: [],
    homebases: [],
    departments: [],
    divisions: [],
    isActive: '',
    sorts: [{ id: 'default', field: 'employeeCode', dir: 'asc' }],
};

const SORT_OPTIONS = [
    { value: 'employeeCode', label: 'Employee Code' },
    { value: 'employeeName', label: 'Employee Name' },
    { value: 'gender',       label: 'Gender' },
    { value: 'workingMode',  label: 'Working Mode' },
    { value: 'rank',         label: 'Rank' },
    { value: 'homeBase',     label: 'Base' },
    { value: 'division',     label: 'Division' },
    { value: 'department',   label: 'Department' },
    { value: 'isActive',     label: 'Status' },
];

let _idCounter = 1;
const newSortId = () => `s${_idCounter++}`;

/* ── Sortable sort row ───────────────────────────────────────────── */
const SortRow = ({ item, onChangeField, onChangeDir, onRemove, canRemove }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
        useSortable({ id: item.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    const currentOption = SORT_OPTIONS.find(o => o.value === item.field) ?? SORT_OPTIONS[0];

    return (
        <Box
            ref={setNodeRef}
            style={style}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                py: 0.5,
            }}
        >
            {/* Drag handle */}
            <Box
                {...attributes}
                {...listeners}
                sx={{ cursor: 'grab', color: 'text.disabled', flexShrink: 0, display: 'flex', alignItems: 'center' }}
            >
                <DragIndicator fontSize="small" />
            </Box>

            {/* Field selector */}
            <Autocomplete
                size="small"
                disableClearable
                options={SORT_OPTIONS}
                getOptionLabel={(o) => o.label}
                isOptionEqualToValue={(o, v) => o.value === v.value}
                value={currentOption}
                onChange={(_, v) => onChangeField(item.id, v.value)}
                sx={{ flex: 1 }}
                renderInput={(params) => <TextField {...params} />}
            />

            {/* Direction toggle */}
            <ToggleButtonGroup
                exclusive
                size="small"
                value={item.dir}
                onChange={(_, v) => v && onChangeDir(item.id, v)}
                sx={{ flexShrink: 0 }}
            >
                <ToggleButton value="asc" sx={{ px: 1 }}>
                    <Tooltip title="A → Z">
                        <ArrowUpward fontSize="inherit" />
                    </Tooltip>
                </ToggleButton>
                <ToggleButton value="desc" sx={{ px: 1 }}>
                    <Tooltip title="Z → A">
                        <ArrowDownward fontSize="inherit" />
                    </Tooltip>
                </ToggleButton>
            </ToggleButtonGroup>

            {/* Remove */}
            <IconButton
                size="small"
                onClick={() => onRemove(item.id)}
                disabled={!canRemove}
                sx={{ flexShrink: 0 }}
            >
                <Close fontSize="small" />
            </IconButton>
        </Box>
    );
};

/* ── Main dialog ─────────────────────────────────────────────────── */
const EmployeeFilterDialog = ({
    open,
    onClose,
    options = {},
    value = EMPLOYEE_FILTER_EMPTY,
    onChange,
}) => {
    const [local, setLocal] = useState(EMPLOYEE_FILTER_EMPTY);

    useEffect(() => {
        if (open) setLocal({ ...EMPLOYEE_FILTER_EMPTY, ...value });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]); // initialize only when dialog opens — not on every value change while open

    const setAuto   = (key) => (_, v) => setLocal(prev => ({ ...prev, [key]: v ?? [] }));
    const setField  = (key) => (e)    => setLocal(prev => ({ ...prev, [key]: e.target.value }));
    const setToggle = (key) => (_, v) => setLocal(prev => ({ ...prev, [key]: v ?? '' }));

    const handleApply = () => { onChange(local); onClose(); };
    const handleClear = () => { onChange(EMPLOYEE_FILTER_EMPTY); onClose(); };

    /* sort operations */
    const handleSortField = (id, field) =>
        setLocal(prev => ({ ...prev, sorts: prev.sorts.map(s => s.id === id ? { ...s, field } : s) }));

    const handleSortDir = (id, dir) =>
        setLocal(prev => ({ ...prev, sorts: prev.sorts.map(s => s.id === id ? { ...s, dir } : s) }));

    const handleSortRemove = (id) =>
        setLocal(prev => ({ ...prev, sorts: prev.sorts.filter(s => s.id !== id) }));

    const handleSortAdd = () =>
        setLocal(prev => ({
            ...prev,
            sorts: [...prev.sorts, { id: newSortId(), field: 'employeeName', dir: 'asc' }],
        }));

    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

    const handleDragEnd = ({ active, over }) => {
        if (!over || active.id === over.id) return;
        setLocal(prev => {
            const oldIndex = prev.sorts.findIndex(s => s.id === active.id);
            const newIndex = prev.sorts.findIndex(s => s.id === over.id);
            return { ...prev, sorts: arrayMove(prev.sorts, oldIndex, newIndex) };
        });
    };

    const showEmployeeTypes = options.employeeTypes?.length > 0;

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Filter & Sort</DialogTitle>
            <DialogContent dividers>
                <Grid container spacing={2}>

                    {/* Code + Name */}
                    <Grid size={{ xs: 12, sm: 3 }}>
                        <TextField
                            size="small" fullWidth label="Code"
                            value={local.employeeCode}
                            onChange={setField('employeeCode')}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 9 }}>
                        <TextField
                            size="small" fullWidth label="Employee Name"
                            value={local.employeeName}
                            onChange={setField('employeeName')}
                        />
                    </Grid>

                    {/* Employee Type (optional) */}
                    {showEmployeeTypes && (
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Autocomplete
                                size="small" multiple disableCloseOnSelect
                                options={options.employeeTypes}
                                value={local.employeeTypes}
                                onChange={setAuto('employeeTypes')}
                                slotProps={{ chip: { size: 'small' } }}
                                renderInput={(params) => <TextField {...params} label="Employee Type" />}
                            />
                        </Grid>
                    )}

                    {/* Gender */}
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Autocomplete
                            size="small" multiple disableCloseOnSelect
                            options={options.genders?.length ? options.genders : ['ML', 'FM']}
                            value={local.genders}
                            onChange={setAuto('genders')}
                            slotProps={{ chip: { size: 'small' } }}
                            renderInput={(params) => <TextField {...params} label="Gender" />}
                        />
                    </Grid>

                    {/* Working Mode */}
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Autocomplete
                            size="small" multiple disableCloseOnSelect
                            options={options.workingModes?.length ? options.workingModes : ['SHIFT', 'OFFICE']}
                            value={local.workingModes}
                            onChange={setAuto('workingModes')}
                            slotProps={{ chip: { size: 'small' } }}
                            renderInput={(params) => <TextField {...params} label="Working Mode" />}
                        />
                    </Grid>

                    {/* Rank + Base */}
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Autocomplete
                            size="small" multiple disableCloseOnSelect
                            options={options.ranks ?? []}
                            value={local.ranks}
                            onChange={setAuto('ranks')}
                            slotProps={{ chip: { size: 'small' } }}
                            renderInput={(params) => <TextField {...params} label="Rank" />}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Autocomplete
                            size="small" multiple disableCloseOnSelect
                            options={options.homebases ?? []}
                            value={local.homebases}
                            onChange={setAuto('homebases')}
                            slotProps={{ chip: { size: 'small' } }}
                            renderInput={(params) => <TextField {...params} label="Base" />}
                        />
                    </Grid>

                    {/* Division + Department */}
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Autocomplete
                            size="small" multiple disableCloseOnSelect
                            options={options.divisions ?? []}
                            value={local.divisions}
                            onChange={setAuto('divisions')}
                            slotProps={{ chip: { size: 'small' } }}
                            renderInput={(params) => <TextField {...params} label="Division" />}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Autocomplete
                            size="small" multiple disableCloseOnSelect
                            options={options.departments ?? []}
                            value={local.departments}
                            onChange={setAuto('departments')}
                            slotProps={{ chip: { size: 'small' } }}
                            renderInput={(params) => <TextField {...params} label="Department" />}
                        />
                    </Grid>

                    {/* Status */}
                    <Grid size={{ xs: 12 }}>
                        <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                            Status
                        </Typography>
                        <ToggleButtonGroup
                            exclusive size="small"
                            value={local.isActive}
                            onChange={setToggle('isActive')}
                        >
                            <ToggleButton value="true">Active</ToggleButton>
                            <ToggleButton value="false">Inactive</ToggleButton>
                        </ToggleButtonGroup>
                    </Grid>

                    {/* ── Sort ──────────────────────────────────────────────────────── */}
                    <Grid size={{ xs: 12 }}>
                        <Divider>
                            <Typography variant="caption" color="text.secondary">Sort</Typography>
                        </Divider>
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={handleDragEnd}
                        >
                            <SortableContext
                                items={local.sorts.map(s => s.id)}
                                strategy={verticalListSortingStrategy}
                            >
                                {local.sorts.map((item) => (
                                    <SortRow
                                        key={item.id}
                                        item={item}
                                        onChangeField={handleSortField}
                                        onChangeDir={handleSortDir}
                                        onRemove={handleSortRemove}
                                        canRemove={local.sorts.length > 1}
                                    />
                                ))}
                            </SortableContext>
                        </DndContext>

                        <Button
                            size="small"
                            startIcon={<Add />}
                            onClick={handleSortAdd}
                            disabled={local.sorts.length >= SORT_OPTIONS.length}
                            sx={{ mt: 0.5 }}
                        >
                            Add sort
                        </Button>
                    </Grid>

                </Grid>
            </DialogContent>

            <DialogActions>
                <Button variant="outlined" color="secondary" onClick={handleClear}>Clear</Button>
                <Button variant="outlined" onClick={handleApply}>Apply</Button>
            </DialogActions>
        </Dialog>
    );
};

export default EmployeeFilterDialog;
